import type { ColorPickerPanelProps } from './colorPicker.tsx';
import type { EditorDialogProps, MapSaveDialogProps } from './editor/dialog.tsx';
import type { EditorOpenMapProps } from './editor/openMap.tsx';
import type { SceneScaleProps } from './sceneScale.tsx';
import type { EditorPerfProps } from './editor/perfPanel.tsx';
import type { EditorToolbarProps } from './editor/toolbar.tsx';
import type { EditorValidationProps } from './editor/validation.tsx';
import type { FpsProps } from './fps.tsx';
import type { FinishPopupProps } from './play/finishPopup.tsx';
import type { PlayDebugProps } from './play/debugPanel.tsx';
import type { RaceHudProps } from './play/raceHud.tsx';
import type { TelemetryStripProps } from './play/telemetryStrip.tsx';
import type { ReplayControlsProps } from './play/replayControls.tsx';
import type { BoatWorkshopProps } from './boats/workshop.tsx';
import type { BotWorkshopProps } from './bots/workshop.tsx';
import type { PauseMenuProps } from './pause/menu.tsx';
import type { MapSelectProps } from './start/mapSelect.tsx';
import { createStore, deepEqual } from './store.ts';

/**
 * The whole overlay is one Preact tree, so every panel lives in a single store rather than
 * mounting itself into `#ui-root`. Scenes still call `mountX(...)` and get back an imperative
 * `{ sync, destroy }` handle; those calls just write a slot here.
 *
 * Play HUD slots: `raceHud`, `telemetry`. Scale: `sceneScale` (bottom-left, every scene).
 * Tool chrome: `playDebug`. See docs/PLAY-HUD.md.
 */
export type OverlayPanels = {
  mapSelect: MapSelectProps | null;
  pauseMenu: PauseMenuProps | null;
  playDebug: PlayDebugProps | null;
  telemetry: TelemetryStripProps | null;
  raceHud: RaceHudProps | null;
  finishPopup: FinishPopupProps | null;
  replayControls: ReplayControlsProps | null;
  editorToolbar: EditorToolbarProps | null;
  boatWorkshop: BoatWorkshopProps | null;
  botWorkshop: BotWorkshopProps | null;
  sceneScale: SceneScaleProps | null;
  editorValidation: EditorValidationProps | null;
  editorPerf: EditorPerfProps | null;
  editorDialog: EditorDialogProps | null;
  editorSaveDialog: MapSaveDialogProps | null;
  editorOpenMap: EditorOpenMapProps | null;
  colorPicker: ColorPickerPanelProps | null;
  fps: FpsProps | null;
};

const EMPTY_PANELS: OverlayPanels = {
  mapSelect: null,
  pauseMenu: null,
  playDebug: null,
  telemetry: null,
  raceHud: null,
  finishPopup: null,
  replayControls: null,
  editorToolbar: null,
  boatWorkshop: null,
  botWorkshop: null,
  sceneScale: null,
  editorValidation: null,
  editorPerf: null,
  editorDialog: null,
  editorSaveDialog: null,
  editorOpenMap: null,
  colorPicker: null,
  fps: null,
};

export const overlayStore = createStore<OverlayPanels>(EMPTY_PANELS);

export function setPanel<K extends keyof OverlayPanels>(id: K, value: OverlayPanels[K]): void {
  const current = overlayStore.get();

  if (deepEqual(current[id], value)) {
    return;
  }

  overlayStore.set({ ...current, [id]: value });
}
